import React from 'react';
import { parse } from 'date-fns';
import {
  WEEKDAY_KEYS,
} from '../../../../shared/booking/employee-schedule/workingSchedule';
import BhTimeSelect from '../../../../components/settings/pages/business-hours/BhTimeSelect';
import {
  dateToTimeInputValue,
  timeInputValueToDate,
} from '../../../../components/settings/pages/business-hours/businessHoursTime';
import '../../../../components/settings/pages/business-hours/BusinessHoursLayout.css';
import '../styles/employee-schedule.css';

const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const timeStringToDate = (timeValue) =>
  timeInputValueToDate(timeValue, new Date()) || parse('09:00', 'HH:mm', new Date());

const dateToTimeString = (dateValue) => dateToTimeInputValue(dateValue) || '09:00';

const EmployeeScheduleEditor = ({
  workingSchedule,
  onScheduleDayChange,
  onCancel,
  onSave,
  saving = false,
}) => (
  <div className="bh-landing employee-schedule">
    <section className="bh-section">
      <h2 className="bh-section-title">Weekly schedule</h2>
      <p className="bh-section-desc">
        Set the recurring days and hours this employee can be booked. Days marked off will be hidden
        during booking.
      </p>

      <div className="bh-day-list">
        {WEEKDAY_KEYS.map((dayKey) => {
          const daySchedule = workingSchedule?.[dayKey];
          const isWorking = Boolean(daySchedule?.isWorking);
          const safeDay = dayKey.replace(/\s+/g, '-');

          return (
            <article
              key={dayKey}
              className={`bh-day-card ${!isWorking ? 'bh-day-card--closed' : ''}`}
            >
              <div className="bh-day-card__header">
                <h3 className="bh-day-name">{DAY_LABELS[dayKey]}</h3>
                <label className="bh-cell-tick">
                  <input
                    type="checkbox"
                    checked={isWorking}
                    onChange={(event) =>
                      onScheduleDayChange(dayKey, 'isWorking', event.target.checked)
                    }
                    aria-label={
                      isWorking
                        ? `${DAY_LABELS[dayKey]}: working, tick to mark off`
                        : `${DAY_LABELS[dayKey]}: off, tick to mark working`
                    }
                  />
                  <span className={`bh-cell-tick__text ${isWorking ? 'is-open' : 'is-closed'}`}>
                    {isWorking ? 'Working' : 'Off'}
                  </span>
                </label>
              </div>

              {!isWorking ? (
                <p className="bh-day-closed-msg">Not available for bookings this day.</p>
              ) : (
                <div className="bh-day-card__body">
                  <div>
                    <div className="bh-block__head">
                      <span className="bh-block__label">Hours</span>
                    </div>
                    <div className="bh-time-row">
                      <BhTimeSelect
                        id={`${safeDay}-start`}
                        label="Start"
                        value={timeStringToDate(daySchedule?.start)}
                        onDateChange={(dateValue) =>
                          onScheduleDayChange(dayKey, 'start', dateToTimeString(dateValue))
                        }
                      />
                      <span className="bh-sep">–</span>
                      <BhTimeSelect
                        id={`${safeDay}-end`}
                        label="End"
                        value={timeStringToDate(daySchedule?.end)}
                        onDateChange={(dateValue) =>
                          onScheduleDayChange(dayKey, 'end', dateToTimeString(dateValue))
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <div className="bh-block__head">
                      <span className="bh-block__label">Break</span>
                    </div>
                    <label
                      className="bh-cell-tick bh-cell-tick--single-line"
                      style={{ marginBottom: daySchedule?.hasBreak ? '0.75rem' : 0 }}
                      title={daySchedule?.hasBreak ? 'Untick to remove break' : 'Tick to add a break'}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(daySchedule?.hasBreak)}
                        onChange={(event) =>
                          onScheduleDayChange(dayKey, 'hasBreak', event.target.checked)
                        }
                        aria-label={`Break on ${DAY_LABELS[dayKey]}`}
                      />
                      <span
                        className={`bh-cell-tick__text ${
                          daySchedule?.hasBreak ? 'is-open' : 'is-closed'
                        }`}
                      >
                        Scheduled break
                      </span>
                    </label>

                    {daySchedule?.hasBreak && (
                      <div className="bh-time-row">
                        <BhTimeSelect
                          id={`${safeDay}-break-start`}
                          label="Start"
                          value={timeStringToDate(daySchedule?.breakStart)}
                          onDateChange={(dateValue) =>
                            onScheduleDayChange(dayKey, 'breakStart', dateToTimeString(dateValue))
                          }
                        />
                        <span className="bh-sep">–</span>
                        <BhTimeSelect
                          id={`${safeDay}-break-end`}
                          label="End"
                          value={timeStringToDate(daySchedule?.breakEnd)}
                          onDateChange={(dateValue) =>
                            onScheduleDayChange(dayKey, 'breakEnd', dateToTimeString(dateValue))
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>

    <div className="bh-actions">
      <button type="button" className="bh-btn bh-btn--ghost" onClick={() => onCancel?.()} disabled={saving}>
        Cancel
      </button>
      <button type="button" className="bh-btn bh-btn--primary" onClick={onSave} disabled={saving}>
        {saving ? (
          <>
            <span className="bh-btn-spinner" aria-hidden />
            Saving…
          </>
        ) : (
          'Save schedule'
        )}
      </button>
    </div>
  </div>
);

export default EmployeeScheduleEditor;
